import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import seoConfig from './seo-config.json';
import {
  getWebSiteSchema,
  getEnhancedLocalBusinessSchema,
  getServiceSchema,
  getBlogPostingSchema,
  getFAQSchema,
} from '@/utils/structuredData';
import { faqData } from '@/data/seoConfig';

const { baseUrl, siteName, defaultOgImage, notFound, dynamicPrefixes, routes } = seoConfig;
const ROBOTS_INDEX = 'index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1';
const ROBOTS_NOINDEX = 'noindex, follow';

const normalize = (pathname) => {
  const p = pathname.replace(/\/+$/, '');
  return p === '' ? '/' : p;
};

const buildSchemas = (path, entry) => {
  const schemas = [];
  switch (entry.schema) {
    case 'home':
      // Nur EIN RealEstateAgent-Objekt (LocalBusiness mit beiden Standorten) + WebSite
      schemas.push(getEnhancedLocalBusinessSchema(), getWebSiteSchema());
      break;
    case 'localbusiness':
      schemas.push(getEnhancedLocalBusinessSchema());
      break;
    case 'service':
      schemas.push(getServiceSchema({ name: entry.h1, description: entry.description, url: path }));
      break;
    case 'standort':
      // BreadcrumbList kommt aus der sichtbaren Breadcrumb-Navigation (eine Quelle, keine Dublette)
      schemas.push(
        getServiceSchema({
          name: entry.h1,
          description: entry.description,
          url: path,
          areaServed: [{ '@type': 'City', name: entry.city }],
        })
      );
      break;
    case 'article':
      schemas.push(
        getBlogPostingSchema({
          title: entry.h1 || entry.title,
          description: entry.description,
          datePublished: entry.lastmod,
          author: entry.author || siteName,
          url: path,
          image: entry.ogImage,
        })
      );
      break;
    default:
      break;
  }
  // Sichtbare FAQ-Blöcke (data/seoConfig.faqData) zusätzlich als FAQPage markieren – zitierfähig für KI-Suche
  const faqs = faqData[path];
  if (faqs?.length && !schemas.some((s) => s['@type'] === 'FAQPage')) {
    schemas.push(getFAQSchema(faqs));
  }
  return schemas;
};

/**
 * Zentrale SEO-Komponente – einzige maßgebliche Quelle für Title, Description,
 * Robots, Canonical, Open Graph, Twitter und JSON-LD aller öffentlichen Routen.
 * Datenquelle: src/seo/seo-config.json (wird auch vom SSG-/Sitemap-Prozess genutzt).
 */
export default function GlobalSEO() {
  const location = useLocation();
  const path = normalize(location.pathname);
  const entry = routes[path];

  useEffect(() => {
    // Statisch vorgerenderte SSG-Head-Tags entfernen, sobald React (Helmet) die
    // Tags selbst verwaltet – verhindert Duplikate nach der Hydration.
    document.querySelectorAll('[data-ssg]').forEach((el) => el.remove());
  }, []);

  if (!entry) {
    const isDynamic = dynamicPrefixes.some((p) => path === p || path.startsWith(p));
    if (isDynamic) {
      return (
        <Helmet>
          <meta name="robots" content={ROBOTS_NOINDEX} />
        </Helmet>
      );
    }
    return (
      <Helmet>
        <title>{notFound.title}</title>
        <meta name="description" content={notFound.description} />
        <meta name="robots" content={ROBOTS_NOINDEX} />
        <meta property="og:title" content={notFound.title} />
        <meta property="og:description" content={notFound.description} />
      </Helmet>
    );
  }

  const canonicalUrl = `${baseUrl}${entry.canonical || (path === '/' ? '/' : path)}`;
  const ogImage = entry.ogImage || defaultOgImage;
  const ogType = entry.schema === 'article' ? 'article' : 'website';
  const schemas = buildSchemas(path, entry);

  return (
    <Helmet>
      <title>{entry.title}</title>
      <meta name="description" content={entry.description} />
      <meta name="robots" content={entry.index ? ROBOTS_INDEX : ROBOTS_NOINDEX} />
      {entry.index && <link rel="canonical" href={canonicalUrl} />}
      {!entry.index && entry.canonical && <link rel="canonical" href={canonicalUrl} />}
      <meta property="og:type" content={ogType} />
      <meta property="og:site_name" content={siteName} />
      <meta property="og:locale" content="de_DE" />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={entry.title} />
      <meta property="og:description" content={entry.description} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={entry.title} />
      <meta name="twitter:description" content={entry.description} />
      <meta name="twitter:image" content={ogImage} />
      {entry.schema === 'article' && entry.lastmod && (
        <meta property="article:published_time" content={entry.lastmod} />
      )}
      {schemas.map((s, i) => (
        <script key={i} type="application/ld+json">{JSON.stringify(s)}</script>
      ))}
    </Helmet>
  );
}
